import React, { FC, useEffect, useState, useCallback } from 'react'
import cn from 'classnames'
import { WbIcon, WbControlError, WbControlHint } from '@workbench/react/dist'
import { FilterOptions } from './FilterOptions'
import { useField } from 'formik'
import classes from './FilterSelect.module.scss'
import { useTranslation } from 'react-i18next'
import { Option } from './types'

interface FilterSelectProps {
  options: Option[]
  label: string
  hint?: string
  value?: string
  name: string
  disabled?: boolean
  defaultValue?: string
}

export const FilterSelect: FC<FilterSelectProps> = ({
  options,
  label,
  hint,
  name,
  disabled = false,
  defaultValue = '',
}) => {
  const { t } = useTranslation()
  const [, { error, value }, { setValue, setTouched }] = useField(name)
  const [open, setOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState<Option>()

  useEffect(() => {
    if (selectedOption || !value) {
      return
    }

    const option = options.find((o) => o.value === value)
    if (option) {
      setSelectedOption(option)
    } else {
      throw new Error('FilterSelect value set without matching option')
    }
  }, [value, options, selectedOption])

  const openOptions = useCallback(() => {
    if (!disabled) {
      setOpen(true)
    }
  }, [disabled, setOpen])

  const closeOptions = useCallback(() => setOpen(false), [setOpen])

  const setSelected = useCallback(
    (option) => {
      setSelectedOption(option)
      setValue(option.value)
      setOpen(false)
      setTouched(true)
    },
    [setSelectedOption, setValue, setOpen, setTouched]
  )
  return (
    <div
      className={cn(classes.container, {
        [classes.open]: open,
        [classes.disabled]: disabled,
      })}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={openOptions}
        className={classes.button}
      >
        <label
          id="pick-up-stations-e2e"
          className={cn(classes.label, {
            [classes.selected]: !!selectedOption,
          })}
        >
          {label}
          <input type="hidden" name={name} value={defaultValue} />
        </label>

        <div className={classes.selectedOption}>{selectedOption?.label}</div>

        <WbIcon className={classes.caret} name="caret-down" />
      </button>

      <FilterOptions
        options={options}
        onSelect={setSelected}
        onRequestClose={closeOptions}
        open={open}
      />

      {hint && <WbControlHint>{t(hint)}</WbControlHint>}
      {error && <WbControlError>{t(error)}</WbControlError>}
    </div>
  )
}
